import React, { useState } from 'react';
import { X, Crown, Check, Zap, Loader2, CreditCard } from 'lucide-react';
import { supabase } from './supabaseClient';

const PRO_FEATURES = [
  'Необмежені об\'єкти, звіти і позиції',
  'Графік робіт (Gantt) + таймлайн',
  'AI-імпорт з PDF, Excel, тексту',
  'Посилання для клієнта + чат',
  'PDF без вотермарку + брендування',
  'Excel-експорт та імпорт',
  'Завантаження файлів і документів',
  'Необмежені шаблони',
  'Довідник: підрядники, експорт, посилання',
  'Google Drive інтеграція',
  'Тендери',
  'Партнерські комісії',
];

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://jkouufahpliqgyvcmbwb.supabase.co';

const PaywallModal = ({ isOpen, onClose, message, trialUsed, onTrialActivated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  // Чи показувати кнопку тріалу (тріал ще не використаний)
  const showTrialButton = !trialUsed;

  // Активувати 14-денний тріал (без оплати)
  const handleStartTrial = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Авторизуйтесь');
        setLoading(false);
        return;
      }

      const trialEnd = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

      const { error: updateError } = await supabase
        .from('subscriptions')
        .upsert({
          user_id: user.id,
          plan: 'pro',
          status: 'trialing',
          trial_ends_at: trialEnd,
          trial_used: true,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (updateError) {
        setError('Помилка активації тріалу');
        console.error('Trial error:', updateError);
      } else {
        if (onTrialActivated) onTrialActivated();
        onClose();
        window.location.reload();
      }
    } catch (err) {
      setError('Помилка зв\'язку з сервером');
      console.error('Trial error:', err);
    }
    setLoading(false);
  };

  // Оплата через Mono
  const handlePayment = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Авторизуйтесь для оплати');
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/mono-create-invoice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      );

      const data = await res.json();

      if (data.pageUrl) {
        window.location.href = data.pageUrl;
      } else {
        setError(data.error || 'Помилка створення платежу');
      }
    } catch (err) {
      setError('Помилка зв\'язку з сервером');
      console.error('Payment error:', err);
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#fff', borderRadius: '16px', maxWidth: '480px', width: '100%',
        boxShadow: '0 25px 50px rgba(0,0,0,0.15)', overflow: 'hidden',
        maxHeight: '95vh', display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #163248 0%, #1e4a6e 100%)',
          padding: '24px 24px 20px', textAlign: 'center', position: 'relative',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 12, right: 12,
              background: 'rgba(255,255,255,0.15)', border: 'none',
              borderRadius: '50%', width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={16} color="#fff" />
          </button>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(249,115,22,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px'
          }}>
            <Crown size={28} color="#f97316" />
          </div>
          <h2 style={{
            color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 8px',
            fontFamily: "'Roboto', sans-serif"
          }}>
            Перейдіть на PRO
          </h2>
          {message && (
            <p style={{ color: '#c8dde6', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
              {message}
            </p>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Features list — 2 columns */}
          <div style={{ 
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 20 
          }}>
            {PRO_FEATURES.map((feat, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#f0fdf4', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1
                }}>
                  <Check size={10} color="#22c55e" strokeWidth={3} />
                </div>
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>{feat}</span>
              </div>
            ))}
          </div>

          {/* Price */}
          <div style={{
            textAlign: 'center', padding: '16px 0', marginBottom: 16,
            borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6'
          }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#163248' }}>
              300 <span style={{ fontSize: 16, fontWeight: 500, color: '#9ca3af' }}>грн/міс</span>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '6px 0 0' }}>
              {showTrialButton
                ? 'Перші 14 днів безкоштовно. Без картки. Скасувати можна в будь-який момент.'
                : 'Оплата карткою Visa/Mastercard. Скасувати можна в будь-який момент.'
              }
            </p>
          </div>

          {/* Error */}
          {error && (
            <p style={{ fontSize: 13, color: '#ef4444', textAlign: 'center', margin: '0 0 12px' }}>{error}</p>
          )}

          {/* CTA — Trial or Payment */}
          {showTrialButton ? (
            <>
              <button
                onClick={handleStartTrial}
                disabled={loading}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px',
                  border: 'none', background: '#f97316', color: '#fff',
                  fontWeight: 700, fontSize: 15, cursor: loading ? 'wait' : 'pointer',
                  fontFamily: 'inherit', transition: 'opacity .15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: loading ? 0.7 : 1,
                }}
                onMouseOver={(e) => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
                onMouseOut={(e) => e.currentTarget.style.opacity = loading ? '0.7' : '1'}
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> Активація...</>
                ) : (
                  <><Zap size={18} /> Спробувати PRO 14 днів безкоштовно</>
                )}
              </button>
              <p style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
                Без введення картки
              </p>
            </>
          ) : (
            <button
              onClick={handlePayment}
              disabled={loading}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                border: 'none', background: '#f97316', color: '#fff',
                fontWeight: 700, fontSize: 15, cursor: loading ? 'wait' : 'pointer',
                fontFamily: 'inherit', transition: 'opacity .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: loading ? 0.7 : 1,
              }}
              onMouseOver={(e) => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={(e) => e.currentTarget.style.opacity = loading ? '0.7' : '1'}
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> Створення платежу...</>
              ) : (
                <><CreditCard size={18} /> Оплатити PRO — 300 грн/міс</>
              )}
            </button>
          )}

          {/* Secondary link */}
          <p style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#9ca3af', fontSize: 13, fontFamily: 'inherit',
                textDecoration: 'underline'
              }}
            >
              Залишитись на FREE
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
